import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import LeftRail from './WorkspaceShell/LeftRail';
import Topbar from './WorkspaceShell/Topbar';
import { useNavGroups } from './WorkspaceShell/useNavGroups';
import type { WorkspaceRole } from '../config/toolsManifest';
import { BreadcrumbSegment } from './WorkspaceShell/BreadcrumbBar';
import ViewAsBanner from '../components/admin/ViewAsBanner';

interface WorkspaceShellProps {
  children: React.ReactNode;
  /** Current user role */
  userRole?: WorkspaceRole;
  /** User display name */
  userName?: string;
  /** User email */
  userEmail?: string;
  /** Badge counts keyed by tool id */
  badgeCounts?: Record<string, number>;
  /** Sign out callback */
  onSignOut?: () => void;
  /** Settings callback */
  onSettings?: () => void;
  /** Command palette toggle callback */
  onCommandPalette?: () => void;
}

/**
 * WorkspaceShell — Single unified layout replacing 5 existing layouts.
 *
 * Replaces:
 * - AppLayout.tsx
 * - AttorneyLayout.tsx
 * - AccountantLayout.tsx
 * - AttorneyRoleLayout.tsx
 * - RoleDashboardLayout.tsx
 *
 * Architecture:
 * - LeftRail (240px expanded / 64px collapsed): Navigation from TOOLS_MANIFEST
 * - Topbar (58px): Wordmark, breadcrumbs, search, theme, role, account
 * - Main content area: Children rendered here
 *
 * Feature flag: WORKSPACE_SHELL (VITE_WORKSPACE_SHELL env var)
 */
export default function WorkspaceShell({
  children,
  userRole = 'analyst',
  userName = 'User',
  userEmail = '',
  badgeCounts = {},
  onSignOut,
  onSettings,
  onCommandPalette,
}: WorkspaceShellProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  // Theme state
  const [theme, setTheme] = useState<'dark' | 'light'>(() => {
    if (typeof window !== 'undefined') {
      // Check localStorage (cg-theme key) or default to dark
      const saved = localStorage.getItem('cg-theme');
      return saved === 'light' ? 'light' : 'dark';
    }
    return 'dark';
  });

  // Apply theme to document
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('cg-theme', theme);
  }, [theme]);

  // Toggle theme
  const handleThemeToggle = useCallback(() => {
    setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  // Build navigation groups from manifest
  const { groups, allItems } = useNavGroups({
    role: userRole,
    badgeCounts,
  });

  // Determine active route
  const activeRoute = useMemo(() => {
    // Find the most specific matching route
    const matchingItems = allItems.filter((item) =>
      item.route && location.pathname.startsWith(item.route.replace(/:\w+/g, ''))
    );

    // Sort by specificity (longest match first)
    matchingItems.sort((a, b) => (b.route?.length || 0) - (a.route?.length || 0));

    return matchingItems[0]?.route || null;
  }, [allItems, location.pathname]);

  // Build breadcrumbs from current location
  const breadcrumbs: BreadcrumbSegment[] = useMemo(() => {
    const segments: BreadcrumbSegment[] = [];

    // Find matching nav item for current route
    const matchingItem = allItems.find((item) =>
      item.route && location.pathname.startsWith(item.route.replace(/:\w+/g, ''))
    );

    if (matchingItem) {
      // Find the group this item belongs to
      const group = groups.find((g) => g.items.some((i) => i.id === matchingItem.id));

      if (group) {
        segments.push({
          label: group.label,
          path: group.items[0]?.route || undefined,
        });
      }

      segments.push({
        label: matchingItem.label,
        path: matchingItem.route || undefined,
        isActive: true,
      });
    }

    return segments;
  }, [allItems, groups, location.pathname]);

  // Navigation handler
  const handleNavigate = useCallback(
    (route: string) => {
      // Fill `:param` tokens from the current route (a `:reportId` token also falls back to
      // `caseId` / `claimId`), so case-scoped rail items resolve on a case page.
      const filled = route
        ? route.replace(/:(\w+)/g, (token, name: string) => {
            const value =
              params[name] ?? (name === 'reportId' ? params.caseId ?? params.claimId : undefined);
            return value ? encodeURIComponent(value) : token;
          })
        : route;
      // Never navigate to a template route whose `:param` was not filled in
      // (it would render the page with the literal ":reportId").
      if (filled && !filled.includes('/:')) {
        navigate(filled);
      }
    },
    [navigate, params]
  );

  // Handle sign out with fallback
  const handleSignOut = useCallback(() => {
    if (onSignOut) {
      onSignOut();
    } else {
      // Default: redirect to sign in
      navigate('/sign-in');
    }
  }, [onSignOut, navigate]);

  // Handle settings with fallback
  const handleSettings = useCallback(() => {
    if (onSettings) {
      onSettings();
    } else {
      navigate('/settings');
    }
  }, [onSettings, navigate]);

  return (
    <div className="min-h-screen flex bg-[var(--color-bg-base)]">
      {/* Left Rail Navigation */}
      <LeftRail
        groups={groups}
        activeRoute={activeRoute}
        onNavigate={handleNavigate}
        userRole={userRole}
      />

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* View-as (impersonation) banner — renders only during impersonation */}
        <ViewAsBanner />
        {/* Topbar */}
        <Topbar
          breadcrumbs={breadcrumbs}
          userRole={userRole}
          userName={userName}
          userEmail={userEmail}
          theme={theme}
          onThemeToggle={handleThemeToggle}
          onNavigate={handleNavigate}
          onSignOut={handleSignOut}
          onCommandPalette={onCommandPalette}
          onSettings={handleSettings}
        />

        {/* Page Content */}
        <main className="flex-1 overflow-auto p-6">
          {children}
        </main>
      </div>
    </div>
  );
}

/**
 * Check if WorkspaceShell feature is enabled.
 * Reads from VITE_WORKSPACE_SHELL env var.
 */
export function isWorkspaceShellEnabled(): boolean {
  // Use type assertion for import.meta.env
  const env = (import.meta as any).env;
  return env?.VITE_WORKSPACE_SHELL === 'true' ||
         env?.VITE_WORKSPACE_SHELL === '1';
}

/**
 * Hook to check if WorkspaceShell is enabled.
 */
export function useWorkspaceShellFlag(): boolean {
  const [enabled, setEnabled] = useState(() => isWorkspaceShellEnabled());

  useEffect(() => {
    // Re-check on mount (in case env changes)
    setEnabled(isWorkspaceShellEnabled());
  }, []);

  return enabled;
}
